import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Music,
  Headphones,
  Lock,
  Sparkles,
  Mic,
  Layers,
  MessageSquare,
  ArrowRight,
  ArrowLeft,
  CheckCircle2,
  ExternalLink,
  ShieldCheck,
  Settings,
} from 'lucide-react';
import { api } from '../app/api';
import { logger } from '../lib/logger';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import { buildSpotifyEmbedUrl, categoryMeta, isFreePlaylist } from '../lib/spotify';
import { APEX_SIX_PREGAME_CARDS } from '../lib/apexSixPregame';
import { useLockerRoomEntitlement } from '../hooks/useLockerRoomEntitlement';
import { LockerRoomPassModal } from '../components/LockerRoomPassModal';
import { AudioDropsShelf } from '../components/AudioDropsShelf';
import { useDocumentHead } from './blog/useDocumentHead';

const SITE_BASE = 'https://apexathleticmind.com';

const PREMIUM_TILES = [
  {
    id: 'ai-coach-full',
    icon: MessageSquare,
    title: 'Full Apex AI Coach',
    blurb: 'Unlimited chat, pre-game prep generator, journal, progress dashboard, and video tools — the complete coaching app, included with your Pass.',
  },
  {
    id: 'audio-drops',
    icon: Mic,
    title: 'Coach Lewis Audio Drops',
    blurb: 'Weekly 90-second locker-room talks. Game-day pump-up, post-loss reset, mid-season grind.',
  },
  {
    id: 'extended-deck',
    icon: Layers,
    title: 'Extended Notecard Deck',
    blurb: '24 cards across pre-game, in-game, post-game, weekly review, off-season, and injury contexts.',
  },
];

function PlaylistMiniCard({ playlist }) {
  const embedUrl = buildSpotifyEmbedUrl(playlist);
  const cat = categoryMeta(playlist.category);
  return (
    <article
      className="rounded-2xl border border-white/10 bg-[#0F1314] overflow-hidden shadow-lg shadow-black/30"
      data-testid={`locker-playlist-${playlist.id}`}
    >
      <div className="px-4 pt-4 pb-3">
        <div className="flex items-center justify-between gap-3 mb-2">
          <span className="font-mono-accent uppercase tracking-[0.2em] text-[9.5px] text-[#4FBCC7] font-semibold">
            {cat.label}
          </span>
          <a
            href={playlist.spotify_url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-[10.5px] text-white/55 hover:text-white transition-colors"
          >
            Spotify <ExternalLink className="h-3 w-3" />
          </a>
        </div>
        <h3 className="font-heading text-[1.05rem] font-bold tracking-tight text-white leading-tight">{playlist.title}</h3>
      </div>
      <div className="px-2 pb-2">
        {embedUrl ? (
          <iframe
            title={`Spotify: ${playlist.title}`}
            src={embedUrl}
            width="100%"
            height="152"
            frameBorder="0"
            allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
            loading="lazy"
            className="rounded-xl bg-black/40"
          />
        ) : null}
      </div>
    </article>
  );
}

function NotecardItem({ card }) {
  return (
    <article
      className="group relative rounded-2xl border border-white/10 bg-gradient-to-br from-[#13191A] to-[#0B0C0D] p-5 shadow-xl shadow-black/40 hover:ring-1 hover:ring-[#4FBCC7]/40 transition-shadow overflow-hidden"
      data-testid={`locker-notecard-${card.number}`}
    >
      {/* corner number */}
      <div className="absolute top-3 right-4 font-mono-accent text-[44px] leading-none text-white/[0.06] font-bold select-none">
        {card.number}
      </div>

      <div className="flex items-center gap-2 mb-3">
        <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-[#004C54] text-white font-mono-accent text-[11px] font-bold">
          {card.number}
        </span>
        <span className="font-mono-accent uppercase tracking-[0.22em] text-[10px] text-[#4FBCC7] font-semibold">
          {card.dimension}
        </span>
      </div>

      <h3 className="font-heading text-[1.15rem] font-bold tracking-tight text-white leading-tight mb-1.5">
        {card.title}
      </h3>
      <p className="text-[12.5px] text-white/55 leading-snug mb-4 italic">
        Pre-game use: {card.pregameUse}
      </p>

      <ul className="space-y-2 mb-4">
        {card.steps.map((s) => (
          <li key={s.label} className="flex gap-2 text-[12.5px] leading-snug">
            <CheckCircle2 className="h-3.5 w-3.5 text-[#4FBCC7] mt-0.5 shrink-0" />
            <div>
              <span className="font-semibold text-white">{s.label}</span>
              <span className="text-white/55"> — {s.detail}</span>
            </div>
          </li>
        ))}
      </ul>

      <div className="font-mono-accent uppercase tracking-[0.18em] text-[9.5px] text-[#4FBCC7]/85 font-semibold border-t border-white/8 pt-3">
        Cue: {card.cue}
      </div>
    </article>
  );
}

function LockedNotecard({ card, onUnlock }) {
  return (
    <button
      type="button"
      onClick={() => onUnlock('extended-deck')}
      className="group text-left relative rounded-2xl border border-white/10 bg-[#0F1314] p-5 shadow-xl shadow-black/40 overflow-hidden hover:ring-1 hover:ring-[#4FBCC7]/40 transition-shadow"
      data-testid={`locker-notecard-locked-${card.number}`}
    >
      {/* corner number — extra faded for locked state */}
      <div className="absolute top-3 right-4 font-mono-accent text-[44px] leading-none text-white/[0.04] font-bold select-none">
        {card.number}
      </div>

      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-white/10 text-white/55 font-mono-accent text-[11px] font-bold">
            {card.number}
          </span>
          <span className="font-mono-accent uppercase tracking-[0.22em] text-[10px] text-white/45 font-semibold">
            {card.dimension}
          </span>
        </div>
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-white/8 text-[9.5px] font-mono-accent uppercase tracking-[0.18em] font-semibold text-white/70">
          <Lock className="h-3 w-3" />
          Premium
        </span>
      </div>

      <h3 className="font-heading text-[1.15rem] font-bold tracking-tight text-white/85 leading-tight mb-1.5">
        {card.title}
      </h3>
      <p className="text-[12.5px] text-white/45 leading-snug mb-4">
        {card.teaser || card.pregameUse}
      </p>

      {/* Blurred / redacted step preview */}
      <div className="space-y-2 mb-4">
        {[0, 1, 2].map((i) => (
          <div key={`bar-${i}`} className="h-2 rounded-full bg-white/8" style={{ width: `${80 - i * 12}%` }} />
        ))}
      </div>

      <div className="flex items-center justify-between border-t border-white/8 pt-3">
        <span className="font-mono-accent uppercase tracking-[0.18em] text-[9.5px] text-white/45 font-semibold">
          Locked
        </span>
        <span className="inline-flex items-center gap-1 text-[11.5px] font-semibold text-[#4FBCC7] group-hover:gap-1.5 transition-all">
          Unlock <ArrowRight className="h-3 w-3" />
        </span>
      </div>
    </button>
  );
}

function LockedPlaylistCard({ playlist, onUnlock }) {
  const cat = categoryMeta(playlist.category);
  return (
    <button
      type="button"
      onClick={() => onUnlock('playlists')}
      className="group text-left relative rounded-2xl border border-white/10 bg-[#0F1314] overflow-hidden shadow-lg shadow-black/30 hover:ring-1 hover:ring-[#4FBCC7]/40 transition-shadow"
      data-testid={`locker-playlist-locked-${playlist.id}`}
    >
      <div className="px-4 pt-4 pb-3">
        <div className="flex items-center justify-between gap-3 mb-2">
          <span className="font-mono-accent uppercase tracking-[0.2em] text-[9.5px] text-white/45 font-semibold">
            {cat.label}
          </span>
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-white/8 text-[9px] font-mono-accent uppercase tracking-[0.18em] font-semibold text-white/70">
            <Lock className="h-3 w-3" />
            Premium
          </span>
        </div>
        <h3 className="font-heading text-[1.05rem] font-bold tracking-tight text-white/85 leading-tight mb-1">
          {playlist.title}
        </h3>
        {playlist.description && (
          <p className="text-[12px] text-white/45 leading-snug line-clamp-2">{playlist.description}</p>
        )}
      </div>
      {/* Faux waveform / locked artwork */}
      <div className="relative h-[124px] mx-2 mb-2 rounded-xl bg-gradient-to-br from-[#004C54]/30 via-[#0B0C0D] to-[#0B0C0D] overflow-hidden flex items-center justify-center">
        <div className="absolute inset-0 flex items-end justify-center gap-[3px] px-6 pb-3 opacity-50">
          {[0.4, 0.6, 0.3, 0.85, 0.55, 0.7, 0.35, 0.92, 0.5, 0.75, 0.4, 0.65, 0.3, 0.88, 0.55, 0.42, 0.7, 0.55, 0.32, 0.6].map((h, i) => (
            <div
              key={`bar-${i}`}
              className="w-[3px] rounded-full bg-[#4FBCC7]/60"
              style={{ height: `${h * 70}%` }}
            />
          ))}
        </div>
        <div className="relative z-10 flex items-center gap-2 px-3 py-1.5 rounded-full bg-black/60 backdrop-blur-sm ring-1 ring-white/15">
          <Lock className="h-3.5 w-3.5 text-[#4FBCC7]" />
          <span className="text-[11px] font-semibold text-white">Unlock to play</span>
        </div>
      </div>
    </button>
  );
}

function PremiumTile({ tile, onClick }) {
  const Icon = tile.icon;
  return (
    <button
      type="button"
      onClick={() => onClick(tile)}
      className="group text-left relative rounded-2xl border border-white/10 bg-[#0F1314] p-5 shadow-xl shadow-black/40 overflow-hidden hover:ring-1 hover:ring-[#4FBCC7]/40 transition-shadow"
      data-testid={`locker-premium-${tile.id}`}
    >
      {/* subtle scrim & lock */}
      <div className="absolute inset-0 bg-gradient-to-br from-black/0 via-black/30 to-black/60 pointer-events-none" />
      <div className="relative">
        <div className="flex items-center justify-between mb-4">
          <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-[#004C54]/70 text-white">
            <Icon className="h-5 w-5" />
          </span>
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-white/8 text-[10px] font-mono-accent uppercase tracking-[0.18em] font-semibold text-white/70">
            <Lock className="h-3 w-3" />
            Premium
          </span>
        </div>
        <h3 className="font-heading text-[1.1rem] font-bold tracking-tight text-white mb-2 leading-tight">
          {tile.title}
        </h3>
        <p className="text-[13px] text-white/55 leading-snug mb-5">{tile.blurb}</p>
        <span className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold text-[#4FBCC7] group-hover:gap-2.5 transition-all">
          Join the Founders Waitlist <ArrowRight className="h-3.5 w-3.5" />
        </span>
      </div>
    </button>
  );
}

function WaitlistModal_DEPRECATED() {
  // Kept as a named no-op for one release cycle to avoid breaking any stale
  // dynamic imports. Active waitlist UX now lives in `LockerRoomPassModal`.
  return null;
}
// eslint-disable-next-line no-unused-vars
const _retainWaitlistModalShim = WaitlistModal_DEPRECATED;

export default function LockerRoom() {
  const [playlists, setPlaylists] = useState(null);
  const [error, setError] = useState(null);
  const [passSource, setPassSource] = useState(null);
  const [portalLoading, setPortalLoading] = useState(false);
  const { isPro, source: entitlementSource } = useLockerRoomEntitlement();

  useEffect(() => {
    if (typeof window !== 'undefined') window.scrollTo({ top: 0, behavior: 'instant' });
    let active = true;
    api.get('/api/playlists')
      .then((data) => { if (active) setPlaylists(data?.playlists || []); })
      .catch((err) => {
        logger.error('LockerRoom: failed to load playlists', err);
        if (active) setError(err.message || 'Could not load playlists');
      });
    return () => { active = false; };
  }, []);

  // Phase 20A freemium gate:
  //   - Playlists: filter by category (pregame + winddown free; others locked).
  //   - Notecards: filter by the `freeAccess` flag on the canonical Apex Six.
  // Pro users see everything as free.
  const { freePlaylists, lockedPlaylists } = useMemo(() => {
    const list = playlists || [];
    if (isPro) return { freePlaylists: list, lockedPlaylists: [] };
    return {
      freePlaylists: list.filter(isFreePlaylist),
      lockedPlaylists: list.filter((p) => !isFreePlaylist(p)),
    };
  }, [playlists, isPro]);

  const { freeCards, lockedCards } = useMemo(() => {
    if (isPro) return { freeCards: APEX_SIX_PREGAME_CARDS, lockedCards: [] };
    return {
      freeCards: APEX_SIX_PREGAME_CARDS.filter((c) => c.freeAccess),
      lockedCards: APEX_SIX_PREGAME_CARDS.filter((c) => !c.freeAccess),
    };
  }, [isPro]);

  /**
   * Open the Stripe Customer Portal so the user can manage / cancel their
   * Locker Room Pass. Only used by the "Manage" button shown when isPro is
   * true. Premium-tier ($199 Coach) holders are routed to /app/dashboard
   * because their billing lives with the main subscription, not the Pass.
   */
  const openManagePortal = async () => {
    if (entitlementSource === 'premium') {
      window.location.assign('/app/dashboard');
      return;
    }
    setPortalLoading(true);
    try {
      const result = await api.post('/api/billing/locker-room-pass/portal', {
        origin_url: window.location.origin,
      });
      if (result?.url) {
        window.location.assign(result.url);
        return;
      }
      throw new Error('Stripe did not return a portal URL');
    } catch (err) {
      logger.error('LockerRoom: portal open failed', err);
      toast.error(err?.message || 'Could not open the subscription portal.');
      setPortalLoading(false);
    }
  };

  useDocumentHead({
    title: 'The Locker Room — Mental Performance Hub | Apex Athletic Mind',
    description: 'Your free mental performance locker: curated Spotify playlists + the Apex Six pre-game notecards. Plus a sneak peek at premium features.',
    canonical: `${SITE_BASE}/locker-room`,
    ogType: 'website',
    jsonLd: {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: 'The Locker Room',
      url: `${SITE_BASE}/locker-room`,
      description: 'Mental performance hub for athletes. Free playlists + Apex Six pre-game notecards.',
    },
  });

  return (
    <main
      className="min-h-screen bg-[#0B0C0D] text-white relative overflow-hidden"
      data-testid="locker-room-page"
    >
      {/* Atmospheric textures — layered radial gradients evoke locker-room
          lighting without resorting to heavy bitmap textures. */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            'radial-gradient(1200px 600px at 12% -10%, rgba(79, 188, 199, 0.10), transparent 60%),' +
            'radial-gradient(900px 500px at 92% 8%, rgba(0, 76, 84, 0.22), transparent 65%),' +
            'radial-gradient(800px 500px at 50% 110%, rgba(79, 188, 199, 0.07), transparent 70%)',
        }}
      />
      {/* faint vertical "locker slats" — subtle stripe pattern */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 opacity-[0.04]"
        style={{
          backgroundImage:
            'repeating-linear-gradient(90deg, rgba(255,255,255,0.6) 0 1px, transparent 1px 96px)',
        }}
      />

      <header className="sticky top-0 z-30 border-b border-white/8 bg-[#0B0C0D]/85 backdrop-blur supports-[backdrop-filter]:bg-[#0B0C0D]/70">
        <div className="max-w-[1200px] mx-auto px-5 sm:px-8 py-4 flex items-center justify-between gap-3">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-[13px] font-medium text-white/70 hover:text-white transition-colors"
            data-testid="locker-back-home"
          >
            <ArrowLeft className="h-4 w-4" />
            Apex Athletic Mind
          </Link>
          {isPro ? (
            <div className="inline-flex items-center gap-2" data-testid="locker-header-pass-active">
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#004C54] ring-1 ring-[#4FBCC7]/40 font-mono-accent text-[10px] uppercase tracking-[0.2em] font-semibold text-white">
                <ShieldCheck className="h-3 w-3 text-[#4FBCC7]" />
                {entitlementSource === 'premium' ? 'Premium Access' : 'Pass Active'}
              </span>
              <button
                type="button"
                onClick={openManagePortal}
                disabled={portalLoading}
                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-white/8 hover:bg-white/14 disabled:opacity-50 text-white/80 hover:text-white font-mono-accent text-[10px] uppercase tracking-[0.2em] font-semibold transition-colors"
                data-testid="locker-header-manage-btn"
                aria-label="Manage subscription"
              >
                <Settings className="h-3 w-3" />
                {portalLoading ? 'Opening…' : 'Manage'}
              </button>
            </div>
          ) : (
            <span className="inline-flex items-center gap-2 font-mono-accent text-[10px] uppercase tracking-[0.22em] text-[#4FBCC7] font-semibold">
              <Headphones className="h-3.5 w-3.5" />
              The Locker Room
            </span>
          )}
        </div>
      </header>

      {/* Hero */}
      <section className="relative max-w-[1200px] mx-auto px-5 sm:px-8 pt-14 sm:pt-20 pb-12">
        <div className="inline-flex items-center gap-2 font-mono-accent uppercase tracking-[0.22em] text-[10px] text-[#4FBCC7] font-semibold mb-4">
          <Sparkles className="h-3.5 w-3.5" />
          Welcome To
        </div>
        <h1
          className="font-heading text-[2.6rem] sm:text-[3.8rem] font-bold tracking-tight leading-[1.02] text-white mb-5 max-w-[18ch]"
          data-testid="locker-room-title"
        >
          The Locker Room.
        </h1>
        <p className="text-[16.5px] sm:text-[18px] text-white/70 leading-[1.7] max-w-[58ch]">
          Where athletes prep. Pre-game playlists, the Apex Six notecards, and the full mental performance locker behind Locker Room Pass.
        </p>
      </section>

      {/* Your Free Locker */}
      <section className="relative max-w-[1200px] mx-auto px-5 sm:px-8 pb-16" data-testid="locker-section-free">
        <div className="flex items-end justify-between flex-wrap gap-3 mb-6">
          <div>
            <div className="font-mono-accent uppercase tracking-[0.22em] text-[10px] text-[#4FBCC7] font-semibold mb-1">
              Your Locker · Free For Every Athlete
            </div>
            <h2 className="font-heading text-[1.7rem] sm:text-[2.1rem] font-bold tracking-tight text-white">
              Open it. It’s yours.
            </h2>
          </div>
        </div>

        {/* Music shelf */}
        <div className="mb-12" data-testid="locker-free-music">
          <div className="flex items-center gap-2 mb-2 flex-wrap">
            <Music className="h-4 w-4 text-[#4FBCC7]" />
            <h3 className="font-heading text-[1.1rem] font-semibold tracking-tight text-white">
              Pre-Game Playlists
            </h3>
            <span
              className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-emerald-500/10 ring-1 ring-emerald-400/30 font-mono-accent uppercase tracking-[0.16em] text-[9px] text-emerald-200 font-semibold"
              data-testid="locker-music-clean-badge"
              title="Every track is verified clean / radio-edit by Coach Lewis."
            >
              <ShieldCheck className="h-2.5 w-2.5" strokeWidth={2.5} />
              Clean Lyrics
            </span>
            <Link to="/playlists" className="ml-auto text-[12.5px] text-white/55 hover:text-white transition-colors">
              See all <ArrowRight className="h-3 w-3 inline-block ml-0.5" />
            </Link>
          </div>
          {error && (
            <div className="rounded-xl border border-red-400/30 bg-red-500/10 p-4 text-[13px] text-red-200">
              {error}
            </div>
          )}
          {!playlists && !error && (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {[0, 1, 2, 3].map((i) => (
                <Skeleton key={`pl-sk-${i}`} className="h-[220px] rounded-2xl bg-white/5" />
              ))}
            </div>
          )}
          {playlists && (freePlaylists.length > 0 || lockedPlaylists.length > 0) && (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4" data-testid="locker-playlist-grid">
              {freePlaylists.map((p) => (
                <PlaylistMiniCard key={p.id} playlist={p} />
              ))}
              {lockedPlaylists.map((p) => (
                <LockedPlaylistCard
                  key={p.id}
                  playlist={p}
                  onUnlock={() => setPassSource(`playlist:${p.category}`)}
                />
              ))}
            </div>
          )}
          {playlists && playlists.length === 0 && (
            <div className="rounded-xl border border-white/10 bg-white/5 p-6 text-center text-[13.5px] text-white/55">
              No playlists loaded yet. Add some from the admin and they’ll appear here.
            </div>
          )}
          {playlists && !isPro && lockedPlaylists.length > 0 && (
            <p className="mt-3 text-[12px] text-white/45 leading-snug" data-testid="locker-playlist-gate-hint">
              Free athletes get 2 playlists. Unlock the full library with{' '}
              <button
                type="button"
                onClick={() => setPassSource('playlist')}
                className="text-[#4FBCC7] hover:text-white underline underline-offset-2 transition-colors font-semibold"
              >
                Locker Room Pass
              </button>.
            </p>
          )}
        </div>

        {/* Audio Drops shelf (Phase 24 — Pass-only premium content) */}
        <AudioDropsShelf
          isPro={isPro}
          onUpgrade={(source) => setPassSource(source || 'audio-drops')}
        />

        {/* Notecards shelf */}
        <div data-testid="locker-free-notecards">
          <div className="flex items-center gap-2 mb-4">
            <Layers className="h-4 w-4 text-[#4FBCC7]" />
            <h3 className="font-heading text-[1.1rem] font-semibold tracking-tight text-white">
              Apex Six · Pre-Game Notecards
            </h3>
          </div>
          <p className="text-[13.5px] text-white/55 max-w-[62ch] mb-5 leading-snug">
            Each card is a 30–120 second mental performance protocol. Free athletes get the first set; the full deck unlocks with Locker Room Pass.
          </p>
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3" data-testid="locker-notecard-grid">
            {APEX_SIX_PREGAME_CARDS.map((c) => {
              // Interleave free + locked variants in their canonical order so
              // free athletes see exactly which numbered cards (#03, #04, #05)
              // are behind the upgrade — creates the strongest "missing piece"
              // signal for conversion.
              const isFree = isPro || c.freeAccess;
              return isFree ? (
                <NotecardItem key={c.number} card={c} />
              ) : (
                <LockedNotecard
                  key={c.number}
                  card={c}
                  onUnlock={() => setPassSource(`extended-deck`)}
                />
              );
            })}
          </div>
          {!isPro && lockedCards.length > 0 && (
            <p className="mt-4 text-[12px] text-white/45 leading-snug" data-testid="locker-notecard-gate-hint">
              {freeCards.length} of {APEX_SIX_PREGAME_CARDS.length} cards open. Unlock the full Apex Six with{' '}
              <button
                type="button"
                onClick={() => setPassSource('extended-deck')}
                className="text-[#4FBCC7] hover:text-white underline underline-offset-2 transition-colors font-semibold"
              >
                Locker Room Pass
              </button>.
            </p>
          )}
        </div>
      </section>

      {/* Premium Lockers */}
      <section className="relative max-w-[1200px] mx-auto px-5 sm:px-8 pb-20" data-testid="locker-section-premium">
        <div className="flex items-end justify-between flex-wrap gap-3 mb-6">
          <div>
            <div className="inline-flex items-center gap-1.5 font-mono-accent uppercase tracking-[0.22em] text-[10px] text-[#4FBCC7] font-semibold mb-1">
              <Lock className="h-3 w-3" />
              Locker Room Pass · $29.99/mo · $299/yr
            </div>
            <h2 className="font-heading text-[1.7rem] sm:text-[2.1rem] font-bold tracking-tight text-white">
              What’s inside when you upgrade.
            </h2>
            <p className="text-[14px] text-white/55 mt-2 max-w-[62ch] leading-snug">
              Tap any tile to lock in Founders pricing. No charge today — Coach Lewis will email you the moment doors open.
            </p>
          </div>
        </div>

        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {PREMIUM_TILES.map((tile) => (
            <PremiumTile key={tile.id} tile={tile} onClick={() => setPassSource(tile.id)} />
          ))}
        </div>

        {/* Bottom universal CTA */}
        <div
          className="mt-12 rounded-3xl bg-gradient-to-br from-[#004C54] via-[#003a40] to-[#0B0C0D] p-8 sm:p-12 ring-1 ring-white/10 shadow-2xl shadow-black/30 text-center"
          data-testid="locker-bottom-cta"
        >
          <div className="font-mono-accent uppercase tracking-[0.22em] text-[10px] text-[#4FBCC7] font-semibold mb-3">
            Locker Room Pass · Founders Pricing
          </div>
          <h3 className="font-heading text-[1.7rem] sm:text-[2.05rem] font-bold tracking-tight leading-[1.1] mb-3 max-w-[26ch] mx-auto">
            $29.99/mo. Or $299/yr and save $61.
          </h3>
          <p className="text-[14.5px] text-white/70 max-w-[52ch] mx-auto leading-snug mb-6">
            Every clean-lyrics playlist. The full Apex Six deck. The complete Apex AI Coach app. Weekly audio drops. Lock in the launch price for life.
          </p>
          <Button
            onClick={() => setPassSource('founders-pass')}
            className="bg-white text-[#0B0C0D] hover:bg-white/90 h-11 px-6 rounded-xl font-semibold"
            data-testid="locker-bottom-cta-btn"
          >
            Join the Founders Waitlist
            <ArrowRight className="h-4 w-4 ml-1" />
          </Button>
        </div>
      </section>

      {passSource && (
        <LockerRoomPassModal source={passSource} onClose={() => setPassSource(null)} />
      )}
    </main>
  );
}
